import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, Users, BarChart3, Settings, Trash2, Edit3, Save, X,
  Shield, Image, MessageCircle, Heart, Sparkles, UserCheck, TrendingUp,
  MessageSquareText, Calendar, Mail, User, Play, Music, ImageIcon,
} from 'lucide-react';
import { BACKEND_URL } from '../utils/api';
import { ArshHeader } from '../components/ArshHeader';

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl p-5 border border-[#D4AF37]/15 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.05) 0%, #05050A 100%)' }}>
    <div className="absolute top-0 right-0 w-16 h-16 rounded-bl-full opacity-10" style={{ background: accent || '#D4AF37' }} />
    <div className="flex items-center justify-between mb-2">
      <Icon size={22} className="text-[#D4AF37]/60" />
      <span className="text-2xl font-['Cormorant_Garamond'] font-light text-white">{value}</span>
    </div>
    <p className="font-['Outfit'] text-sm text-white/40">{label}</p>
  </div>
);

const catColor = (cat) => ({
  fashion: 'bg-[#D4AF37]', food: 'bg-[#D4AF37]/80', explore: 'bg-[#0F52BA]',
  souls: 'bg-[#D4AF37]/60', sporty: 'bg-[#10B981]', success: 'bg-[#D4AF37]', legacy: 'bg-[#D4AF37]/70',
}[cat] || 'bg-[#B8926A]');

const StatsTab = ({ stats }) => {
  if (!stats) return <div className="text-center py-10 text-white/30 font-['Outfit']">Loading stats...</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Users} label="Total Users" value={stats.total_users} accent="#D4AF37" />
        <StatCard icon={Image} label="Total Vibes" value={stats.total_memories} accent="#0F52BA" />
        <StatCard icon={MessageCircle} label="Comments" value={stats.total_comments} accent="#D4AF37" />
        <StatCard icon={Heart} label="Reactions" value={stats.total_reactions} accent="#D4AF37" />
        <StatCard icon={Sparkles} label="Try Ads" value={stats.total_try_posts} accent="#10B981" />
        <StatCard icon={UserCheck} label="New (7d)" value={stats.recent_signups} accent="#B8926A" />
      </div>
      <div className="rounded-2xl p-5 border border-[#D4AF37]/10" style={{ background: '#05050A' }}>
        <h3 className="font-['Cormorant_Garamond'] text-base font-light text-white mb-4 flex items-center gap-2">
          <TrendingUp size={18} className="text-[#D4AF37]" /> Category Breakdown
        </h3>
        <div className="space-y-3">
          {Object.entries(stats.category_counts || {}).map(([cat, count]) => {
            const max = Math.max(...Object.values(stats.category_counts || {}), 1);
            const pct = Math.round((count / max) * 100);
            return (
              <div key={cat}>
                <div className="flex justify-between font-['Outfit'] text-sm mb-1">
                  <span className="capitalize text-white/70">{cat}</span>
                  <span className="text-white/30">{count}</span>
                </div>
                <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${catColor(cat)} transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const UsersTab = ({ users, onDelete, onEdit }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const startEdit = (user) => { setEditingId(user.id); setEditForm({ nickname: user.nickname, life_quote: user.life_quote || '', is_public: user.is_public }); };
  const handleSave = async (userId) => { await onEdit(userId, editForm); setEditingId(null); };
  if (!users) return <div className="text-center py-10 text-white/30 font-['Outfit']">Loading...</div>;
  const filtered = users.filter((u) => !searchQuery || u.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase()));
  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-[#D4AF37]/15 bg-white/5 font-['Outfit'] text-sm text-white/90 placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40";
  return (
    <div className="space-y-3">
      <input type="text" placeholder="Search by name or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className={inputCls} data-testid="admin-users-search" />
      <p className="font-['Outfit'] text-xs text-white/30">{filtered.length} of {users.length} users</p>
      {filtered.map((user) => (
        <div key={user.id} className="rounded-xl border border-[#D4AF37]/10 p-4" style={{ background: 'rgba(212,175,55,0.02)' }} data-testid={`admin-user-${user.id}`}>
          <div className="flex items-start gap-3">
            {user.profile_picture ? (
              <img src={user.profile_picture} alt="" className="w-12 h-12 rounded-full object-cover shrink-0 border border-[#D4AF37]/20" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#05050A] flex items-center justify-center text-[#D4AF37] font-bold text-lg shrink-0 border border-[#D4AF37]/20">
                {(user.nickname || user.email)?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              {editingId === user.id ? (
                <div className="space-y-2">
                  <input value={editForm.nickname} onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })} placeholder="Nickname" className={inputCls} data-testid={`edit-nickname-${user.id}`} />
                  <input value={editForm.life_quote} onChange={(e) => setEditForm({ ...editForm, life_quote: e.target.value })} placeholder="Life quote" className={`${inputCls} text-xs`} />
                  <label className="flex items-center gap-2 font-['Outfit'] text-xs text-white/30">
                    <input type="checkbox" checked={editForm.is_public} onChange={(e) => setEditForm({ ...editForm, is_public: e.target.checked })} className="accent-[#D4AF37]" /> Public profile
                  </label>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <p className="font-['Outfit'] text-sm font-semibold text-white/80">{user.nickname || 'No name set'}</p>
                    {user.role === 'admin' && <span className="px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] font-['Outfit'] text-[10px] font-bold uppercase">Admin</span>}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5"><Mail size={12} className="text-white/20" /><p className="font-['Outfit'] text-xs text-white/30">{user.email}</p></div>
                  {user.life_quote && <p className="font-['Outfit'] text-xs text-[#D4AF37]/50 mt-1 italic">"{user.life_quote}"</p>}
                </>
              )}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {user.role !== 'admin' && (editingId === user.id ? (
                <><button onClick={() => handleSave(user.id)} className="p-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20" data-testid={`save-user-${user.id}`}><Save size={16} /></button>
                <button onClick={() => setEditingId(null)} className="p-1.5 rounded-lg bg-white/5 text-white/30 hover:bg-white/10"><X size={16} /></button></>
              ) : (
                <><button onClick={() => startEdit(user)} className="p-1.5 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20" data-testid={`edit-user-${user.id}`}><Edit3 size={16} /></button>
                <button onClick={() => onDelete(user.id, user.nickname || user.email)} className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid={`delete-user-${user.id}`}><Trash2 size={16} /></button></>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5">
            <div className="flex items-center gap-1 font-['Outfit'] text-xs text-white/30"><Image size={12} /><span>{user.memory_count} vibes</span></div>
            <div className="flex items-center gap-1 font-['Outfit'] text-xs text-white/30"><MessageCircle size={12} /><span>{user.comment_count} comments</span></div>
            <div className="flex items-center gap-1 font-['Outfit'] text-xs text-white/30"><User size={12} /><span>{user.is_public ? 'Public' : 'Private'}</span></div>
            {user.created_at && <div className="flex items-center gap-1 font-['Outfit'] text-xs text-white/30"><Calendar size={12} /><span>{new Date(user.created_at).toLocaleDateString()}</span></div>}
          </div>
        </div>
      ))}
    </div>
  );
};

const VibesTab = ({ vibes, onDelete }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  if (!vibes) return <div className="text-center py-10 text-white/30 font-['Outfit']">Loading...</div>;
  const filtered = vibes.filter((v) => {
    const matchSearch = !searchQuery || v.user_nickname?.toLowerCase().includes(searchQuery.toLowerCase()) || v.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) || v.note?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = filterCat === 'all' || v.category === filterCat;
    return matchSearch && matchCat;
  });
  const categories = ['all', 'fashion', 'food', 'explore', 'souls', 'sporty', 'success', 'legacy'];
  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-[#D4AF37]/15 bg-white/5 font-['Outfit'] text-sm text-white/90 placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40";
  return (
    <div className="space-y-3">
      <input type="text" placeholder="Search by user or note..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className={inputCls} data-testid="admin-vibes-search" />
      <div className="flex gap-1.5 flex-wrap">
        {categories.map((cat) => (
          <button key={cat} onClick={() => setFilterCat(cat)}
            className={`px-3 py-1 rounded-full font-['Outfit'] text-xs font-medium capitalize transition-all ${filterCat === cat ? 'bg-[#D4AF37] text-black' : 'border border-white/10 text-white/40'}`}
            data-testid={`vibes-filter-${cat}`}>{cat}</button>
        ))}
      </div>
      <p className="font-['Outfit'] text-xs text-white/30">{filtered.length} vibes</p>
      {filtered.length === 0 ? (
        <div className="text-center py-10"><ImageIcon size={40} className="mx-auto mb-3 text-white/10" /><p className="font-['Outfit'] text-sm text-white/30">No vibes found</p></div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {filtered.map((v, idx) => (
            <div key={v.id || idx} className="rounded-xl border border-[#D4AF37]/10 overflow-hidden" style={{ background: '#05050A' }} data-testid={`admin-vibe-${idx}`}>
              <div className="relative aspect-square" style={{ background: '#0a0a0f' }}>
                {v.media_type === 'video' ? (
                  <div className="w-full h-full flex items-center justify-center">
                    {v.image_url && <video src={v.image_url} className="w-full h-full object-cover" muted />}
                    <div className="absolute inset-0 flex items-center justify-center"><Play size={28} className="text-white drop-shadow-lg" /></div>
                  </div>
                ) : v.media_type === 'audio' ? (
                  <div className="w-full h-full flex items-center justify-center bg-[#D4AF37]/5">
                    <Music size={32} className="text-[#D4AF37]/50" />
                  </div>
                ) : (
                  v.image_url && <img src={v.image_url} alt="" className="w-full h-full object-cover" />
                )}
                <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-black font-['Outfit'] text-[10px] font-bold uppercase ${catColor(v.category)}`}>{v.category}</span>
                <button onClick={() => onDelete(v.id, v.user_nickname)} className="absolute top-2 right-2 p-1 rounded-full bg-red-500/80 text-white hover:bg-red-600 transition-all" data-testid={`delete-vibe-${idx}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="p-3">
                <p className="font-['Outfit'] text-xs font-semibold text-white/70 truncate">{v.user_nickname || 'Unknown'}</p>
                <p className="font-['Outfit'] text-[10px] text-white/20 truncate">{v.user_email}</p>
                {v.note && <p className="font-['Outfit'] text-[11px] text-white/40 mt-1 line-clamp-2">{v.note}</p>}
                <div className="flex items-center gap-2 mt-2">
                  <span className="flex items-center gap-0.5 font-['Outfit'] text-[10px] text-white/20"><Heart size={10} />{v.like_count}</span>
                  <span className="flex items-center gap-0.5 font-['Outfit'] text-[10px] text-white/20"><MessageCircle size={10} />{v.comment_count}</span>
                  <span className="flex items-center gap-0.5 font-['Outfit'] text-[10px] text-white/20"><Sparkles size={10} />{v.reaction_count}</span>
                </div>
                {v.created_at && <p className="font-['Outfit'] text-[10px] text-[#D4AF37]/40 mt-1">{new Date(v.created_at).toLocaleDateString()}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CommentsTab = ({ comments, onDelete }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  if (!comments) return <div className="text-center py-10 text-white/30 font-['Outfit']">Loading...</div>;
  const filtered = comments.filter((c) => {
    const matchSearch = !searchQuery || c.text?.toLowerCase().includes(searchQuery.toLowerCase()) || c.user_nickname?.toLowerCase().includes(searchQuery.toLowerCase()) || c.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchType = filterType === 'all' || c.type === filterType;
    return matchSearch && matchType;
  });
  const commentCount = comments.filter(c => c.type === 'comment').length;
  const reactionCount = comments.filter(c => c.type === 'reaction').length;
  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-[#D4AF37]/15 bg-white/5 font-['Outfit'] text-sm text-white/90 placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40";
  return (
    <div className="space-y-3">
      <input type="text" placeholder="Search by text, user name, or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className={inputCls} data-testid="admin-comments-search" />
      <div className="flex gap-2">
        {[{ key: 'all', label: `All (${comments.length})` }, { key: 'comment', label: `Comments (${commentCount})` }, { key: 'reaction', label: `Reactions (${reactionCount})` }].map((f) => (
          <button key={f.key} onClick={() => setFilterType(f.key)}
            className={`px-3 py-1.5 rounded-full font-['Outfit'] text-xs font-medium transition-all ${filterType === f.key ? 'bg-[#D4AF37] text-black' : 'border border-white/10 text-white/40'}`}
            data-testid={`filter-${f.key}`}>{f.label}</button>
        ))}
      </div>
      <p className="font-['Outfit'] text-xs text-white/30">{filtered.length} items</p>
      {filtered.length === 0 ? (
        <div className="text-center py-10"><MessageSquareText size={40} className="mx-auto mb-3 text-white/10" /><p className="font-['Outfit'] text-sm text-white/30">Nothing here yet</p></div>
      ) : (
        filtered.map((c, idx) => (
          <div key={c.id || idx} className="rounded-xl border border-[#D4AF37]/10 p-4" style={{ background: 'rgba(212,175,55,0.02)' }} data-testid={`admin-comment-${idx}`}>
            <div className="flex items-start gap-3">
              {c.memory_thumbnail && <img src={c.memory_thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0 border border-[#D4AF37]/10" />}
              <div className="flex-1 min-w-0">
                {c.type === 'reaction' ? (
                  <div className="rounded-xl px-4 py-3 mb-2 flex items-center gap-2 border border-[#D4AF37]/10" style={{ background: 'rgba(212,175,55,0.05)' }}>
                    <span className="text-2xl">{c.text}</span>
                    <span className="font-['Outfit'] text-sm text-[#D4AF37] font-medium">Reacted</span>
                  </div>
                ) : (
                  <div className="rounded-xl px-4 py-3 mb-2 border border-white/5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="font-['Outfit'] text-sm text-white/70 leading-relaxed">{c.text}</p>
                  </div>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1"><User size={12} className="text-[#D4AF37]" /><span className="font-['Outfit'] text-xs font-medium text-white/70">{c.user_nickname || 'Unknown'}</span></div>
                  <span className="font-['Outfit'] text-xs text-white/20">{c.user_email}</span>
                  {c.memory_category && <span className={`px-2 py-0.5 rounded-full font-['Outfit'] text-[10px] font-bold uppercase text-black ${catColor(c.memory_category)}`}>{c.memory_category}</span>}
                  {c.created_at && <span className="font-['Outfit'] text-xs text-white/20">{new Date(c.created_at).toLocaleDateString()} {new Date(c.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>}
                </div>
              </div>
              {c.type === 'comment' && (
                <button onClick={() => onDelete(c.id, c.text)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all shrink-0" data-testid={`delete-comment-${idx}`}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const SettingsTab = ({ settings, onSave }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);
  if (!form) return <div className="text-center py-10 text-white/30 font-['Outfit']">Loading...</div>;
  const handleSave = async () => { setSaving(true); await onSave(form); setSaving(false); };
  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#D4AF37]/15 bg-white/5 font-['Outfit'] text-sm text-white/90 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40";
  const Toggle = ({ label, field }) => (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <span className="font-['Outfit'] text-sm text-white/70">{label}</span>
      <button onClick={() => setForm({ ...form, [field]: !form[field] })} className={`w-11 h-6 rounded-full transition-colors ${form[field] ? 'bg-[#D4AF37]' : 'bg-white/10'}`} data-testid={`toggle-${field}`}>
        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${form[field] ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="rounded-2xl p-5 border border-[#D4AF37]/10" style={{ background: '#05050A' }}>
        <h3 className="font-['Cormorant_Garamond'] text-base font-light text-white mb-4">App Configuration</h3>
        <div><label className="block font-['Outfit'] text-[10px] uppercase tracking-[0.15em] font-bold text-[#D4AF37]/60 mb-1">App Name</label>
          <input value={form.app_name || ''} onChange={(e) => setForm({ ...form, app_name: e.target.value })} className={inputCls} data-testid="settings-app-name" /></div>
        <div className="mt-3"><label className="block font-['Outfit'] text-[10px] uppercase tracking-[0.15em] font-bold text-[#D4AF37]/60 mb-1">Max Upload Size (MB)</label>
          <input type="number" value={form.max_upload_size_mb || 500} onChange={(e) => setForm({ ...form, max_upload_size_mb: parseInt(e.target.value) || 500 })} className={inputCls} data-testid="settings-max-upload" /></div>
      </div>
      <div className="rounded-2xl p-5 border border-[#D4AF37]/10" style={{ background: '#05050A' }}>
        <h3 className="font-['Cormorant_Garamond'] text-base font-light text-white mb-2">Feature Toggles</h3>
        <Toggle label="Allow New Registrations" field="allow_registration" />
        <Toggle label="Public Discover Feed" field="allow_public_discover" />
        <Toggle label="Maintenance Mode" field="maintenance_mode" />
      </div>
      <button onClick={handleSave} disabled={saving} className="w-full bg-[#D4AF37] text-black py-3 rounded-full font-['Outfit'] font-semibold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50" data-testid="save-settings-button">
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
};

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [vibes, setVibes] = useState(null);
  const [comments, setComments] = useState(null);
  const [settings, setSettings] = useState(null);
  const isAdmin = user?.role === 'admin';

  const fetchStats = useCallback(async () => { try { const { data } = await axios.get(`${BACKEND_URL}/api/admin/stats`); setStats(data); } catch (err) { console.error('Failed to fetch stats:', err); } }, []);
  const fetchUsers = useCallback(async () => { try { const { data } = await axios.get(`${BACKEND_URL}/api/admin/users`); setUsers(data); } catch (err) { console.error('Failed to fetch users:', err); } }, []);
  const fetchVibes = useCallback(async () => { try { const { data } = await axios.get(`${BACKEND_URL}/api/admin/vibes`); setVibes(data); } catch (err) { console.error('Failed to fetch vibes:', err); } }, []);
  const fetchComments = useCallback(async () => { try { const { data } = await axios.get(`${BACKEND_URL}/api/admin/comments`); setComments(data); } catch (err) { console.error('Failed to fetch comments:', err); } }, []);
  const fetchSettings = useCallback(async () => { try { const { data } = await axios.get(`${BACKEND_URL}/api/admin/settings`); setSettings(data); } catch (err) { console.error('Failed to fetch settings:', err); } }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchStats(); fetchUsers(); fetchVibes(); fetchComments(); fetchSettings();
  }, [isAdmin, fetchStats, fetchUsers, fetchVibes, fetchComments, fetchSettings]);

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`Delete "${name}" and ALL their data?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`); setUsers((p) => p.filter((u) => u.id !== userId)); fetchStats(); } catch (err) { console.error('Failed to delete user:', err); }
  };
  const handleEditUser = async (userId, changes) => { try { await axios.put(`${BACKEND_URL}/api/admin/users/${userId}`, changes); fetchUsers(); } catch (err) { console.error('Failed to edit user:', err); } };
  const handleDeleteVibe = async (vibeId, userName) => {
    if (!window.confirm(`Delete this vibe by "${userName}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/admin/vibes/${vibeId}`); setVibes((p) => p.filter((v) => v.id !== vibeId)); fetchStats(); } catch (err) { console.error('Failed to delete vibe:', err); }
  };
  const handleDeleteComment = async (commentId, text) => {
    const preview = text?.length > 40 ? text.slice(0, 40) + '...' : text;
    if (!window.confirm(`Delete this comment?\n\n"${preview}"`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/admin/comments/${commentId}`); setComments((p) => p.filter((c) => c.id !== commentId)); fetchStats(); } catch (err) { console.error('Failed to delete comment:', err); }
  };
  const handleSaveSettings = async (s) => { try { await axios.put(`${BACKEND_URL}/api/admin/settings`, s); fetchSettings(); } catch (err) { console.error('Failed to save settings:', err); } };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#000000' }}>
        <div className="text-center">
          <Shield size={48} className="mx-auto mb-4 text-[#D4AF37]" />
          <h2 className="font-['Cormorant_Garamond'] text-xl text-white mb-2">Admin Access Only</h2>
          <p className="font-['Outfit'] text-sm text-white/30 mb-4">This page is restricted to the admin account.</p>
          <button onClick={() => navigate('/')} className="text-[#D4AF37] font-['Outfit'] font-medium hover:underline">Go Home</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'stats', label: 'Stats', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'vibes', label: 'Vibes', icon: ImageIcon },
    { id: 'comments', label: 'Comments', icon: MessageSquareText },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#000000' }}>
      <div className="sticky top-0 z-30 border-b border-[#D4AF37]/10" style={{ background: 'rgba(5,5,10,0.9)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 rounded-xl hover:bg-white/5 transition-colors" data-testid="admin-back-button"><ArrowLeft size={20} className="text-white/50" /></button>
          <div className="flex-1"><h1 className="font-['Cormorant_Garamond'] text-lg font-light text-white">Admin Panel</h1><p className="font-['Outfit'] text-xs text-white/20">{user?.email}</p></div>
          <Shield size={20} className="text-[#D4AF37]" />
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-3">
        <div className="flex gap-1 rounded-2xl p-1 border border-[#D4AF37]/10 overflow-x-auto" style={{ background: '#05050A' }}>
          {tabs.map((t) => {
            const Icon = t.icon; const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-xl font-['Outfit'] text-[11px] sm:text-xs font-medium transition-all whitespace-nowrap ${active ? 'bg-[#D4AF37] text-black shadow-[0_0_15px_rgba(212,175,55,0.2)]' : 'text-white/30'}`}
                data-testid={`admin-tab-${t.id}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 pb-8">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
            {tab === 'stats' && <StatsTab stats={stats} />}
            {tab === 'users' && <UsersTab users={users} onDelete={handleDeleteUser} onEdit={handleEditUser} />}
            {tab === 'vibes' && <VibesTab vibes={vibes} onDelete={handleDeleteVibe} />}
            {tab === 'comments' && <CommentsTab comments={comments} onDelete={handleDeleteComment} />}
            {tab === 'settings' && <SettingsTab settings={settings} onSave={handleSaveSettings} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
